import {
  Box, Container, Heading, SimpleGrid, Stat, StatLabel, StatNumber,
  StatHelpText, Card, CardBody, VStack, HStack, Text, Badge,
  Spinner, Alert, AlertIcon, Table, Thead, Tbody, Tr, Th, Td,
  Button, Icon, IconButton, Tabs, TabList, TabPanels, Tab, TabPanel
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getListasComprasStats } from "../services/api";
import { FiShoppingCart, FiClock, FiCheckCircle, FiAlertCircle, FiPackage, FiArrowLeft } from "react-icons/fi";
import ConfiguracoesListaCompras from "../components/ConfiguracoesListaCompras";

export default function ListasComprasDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      const data = await getListasComprasStats();
      if (data) {
        setStats(data);
      }
      setLoading(false);
    };
    fetchStats();
  }, []);

  return (
    <Box minH="100vh" bg="#f0f0f0" ml={{ base: 0, md: "64px" }} pt={{ base: "60px", md: 6 }} pb={6} px={4}>
      <Container maxW="container.xl">
        {/* Header */}
        <HStack justifyContent="space-between" mb={6} flexWrap="wrap">
          <HStack spacing={2}>
            <IconButton
              icon={<FiArrowLeft />}
              onClick={() => navigate(-1)}
              variant="ghost"
              aria-label="Voltar"
              size="md"
            />
            <Heading size="lg" color="#01695E">Dashboard - Listas de Compras</Heading>
          </HStack>
          <HStack spacing={3}>
            <Button
              colorScheme="purple"
              variant="outline"
              onClick={() => navigate('/listas-compras/kanban')}
            >
              Kanban
            </Button>
            <Button
              colorScheme="orange"
              bg="#F68633"
              onClick={() => navigate('/listas-compras')}
            >
              Ver Todas as Listas
            </Button>
            <Button
              colorScheme="green"
              bg="#01695E"
              onClick={() => navigate('/listas-compras/nova')}
            >
              + Nova Lista
            </Button>
          </HStack>
        </HStack>

        <Tabs colorScheme="orange" variant="enclosed" bg="white" borderRadius="md" boxShadow="sm">
          <TabList>
            <Tab fontWeight="bold">Visão Geral</Tab>
            <Tab fontWeight="bold">Configurações</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <VisaoGeral loading={loading} stats={stats} navigate={navigate} />
            </TabPanel>
            <TabPanel>
              <ConfiguracoesListaCompras />
            </TabPanel>
          </TabPanels>
        </Tabs>

        <Box mt={8} textAlign="center" color="gray.600">
          <Text fontSize="sm">© {new Date().getFullYear()} TAGA Auto Peças - Sistema de Lista de Compras</Text>
        </Box>
      </Container>
    </Box>
  );
}

function VisaoGeral({ loading, stats, navigate }) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="300px">
        <VStack spacing={4}>
          <Spinner size="xl" color="#F68633" thickness="4px" />
          <Text color="#01695E">Carregando estatísticas...</Text>
        </VStack>
      </Box>
    );
  }

  if (!stats) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        Erro ao carregar estatísticas
      </Alert>
    );
  }

  return (
    <>
      {/* Cards de Estatísticas */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4} mb={6}>
        <Card boxShadow="lg" bg="white">
          <CardBody>
            <Stat>
              <HStack spacing={3} mb={2}>
                <Icon as={FiShoppingCart} boxSize={6} color="#F68633" />
                <StatLabel fontSize="sm" color="gray.600">Total de Listas</StatLabel>
              </HStack>
              <StatNumber fontSize="3xl" color="#01695E">{stats.total_listas || 0}</StatNumber>
              <StatHelpText>Todas as listas criadas</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card boxShadow="lg" bg="white">
          <CardBody>
            <Stat>
              <HStack spacing={3} mb={2}>
                <Icon as={FiClock} boxSize={6} color="gray.500" />
                <StatLabel fontSize="sm" color="gray.600">Pendentes</StatLabel>
              </HStack>
              <StatNumber fontSize="3xl" color="gray.600">{stats.listas_pendentes || 0}</StatNumber>
              <StatHelpText>Aguardando resposta</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card boxShadow="lg" bg="white">
          <CardBody>
            <Stat>
              <HStack spacing={3} mb={2}>
                <Icon as={FiAlertCircle} boxSize={6} color="orange.500" />
                <StatLabel fontSize="sm" color="gray.600">Respondidas</StatLabel>
              </HStack>
              <StatNumber fontSize="3xl" color="orange.500">{stats.listas_respondidas || 0}</StatNumber>
              <StatHelpText>Aguardando aprovação</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card boxShadow="lg" bg="white">
          <CardBody>
            <Stat>
              <HStack spacing={3} mb={2}>
                <Icon as={FiCheckCircle} boxSize={6} color="green.500" />
                <StatLabel fontSize="sm" color="gray.600">Aprovadas</StatLabel>
              </HStack>
              <StatNumber fontSize="3xl" color="green.500">{stats.listas_aprovadas || 0}</StatNumber>
              <StatHelpText>Finalizadas</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card boxShadow="lg" bg="white">
          <CardBody>
            <Stat>
              <HStack spacing={3} mb={2}>
                <Icon as={FiPackage} boxSize={6} color="red.500" />
                <StatLabel fontSize="sm" color="gray.600">Itens Urgentes</StatLabel>
              </HStack>
              <StatNumber fontSize="3xl" color="red.500">{stats.itens_urgentes || 0}</StatNumber>
              <StatHelpText>Requerem atenção</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Card de Listas de Hoje */}
      <Card mb={6} boxShadow="lg">
        <CardBody>
          <HStack justifyContent="space-between" mb={4}>
            <Heading size="md" color="#01695E">Listas de Hoje</Heading>
            <Badge colorScheme="orange" fontSize="lg" px={3} py={1}>
              {stats.listas_hoje || 0} listas
            </Badge>
          </HStack>
          <Text color="gray.600">
            {stats.listas_hoje > 0
              ? `Você tem ${stats.listas_hoje} lista(s) criada(s) hoje.`
              : "Nenhuma lista criada hoje."}
          </Text>
        </CardBody>
      </Card>

      {/* Tabela por Fornecedor */}
      {stats.por_fornecedor && stats.por_fornecedor.length > 0 && (
        <Card boxShadow="lg">
          <CardBody>
            <Heading size="md" color="#01695E" mb={4}>Listas por Fornecedor</Heading>
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Fornecedor</Th>
                    <Th isNumeric>Total de Listas</Th>
                    <Th>Ação</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {stats.por_fornecedor.map((item) => (
                    <Tr key={item.fornecedor_id} _hover={{ bg: "gray.50" }}>
                      <Td fontWeight="bold">
                        {item.fornecedor?.pessoa?.nome || `Fornecedor #${item.fornecedor_id}`}
                      </Td>
                      <Td isNumeric>
                        <Badge colorScheme="blue" fontSize="md" px={2}>
                          {item.total}
                        </Badge>
                      </Td>
                      <Td>
                        <Button
                          size="sm"
                          colorScheme="orange"
                          variant="outline"
                          onClick={() => navigate(`/listas-compras?fornecedor_id=${item.fornecedor_id}`)}
                        >
                          Ver Listas
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </CardBody>
        </Card>
      )}
    </>
  );
}
